// Guest RSVP Component
// Handles the two-step verification flow for guests to RSVP to events
import { escapeHtml } from '../utils/html-utils.js';

class GuestRSVP extends HTMLElement {
  constructor() {
    super();
    this.token = null;
    this.magicToken = null;
    this.event = null;
    this.invitation = null;
    this.guestInfo = null;
    this.step = 'verify'; // verify, request-link, authenticate, rsvp
    this.loading = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.logger = window.logger || console;
    this.magicTokenProcessed = false;
  }

  connectedCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    this.token = this.getAttribute('token') || urlParams.get('token');
    this.magicToken = urlParams.get('magicToken');

    const isGuestAccessRoute = window.location.pathname.startsWith('/events/guest-access');

    if (!this.token && this.magicToken && isGuestAccessRoute) {
      this.step = 'authenticate';
      this.render();
      return;
    }

    if (this.token) {
      this.verifyToken();
    } else {
      this.renderError('No invitation token provided');
    }
  }

  async verifyToken() {
    try {
      if (!this.token) {
        this.renderError('Missing invitation token');
        return;
      }

      this.loading = true;
      this.render();

      const data = await this.apiRequest(`/events/guest-verify/${encodeURIComponent(this.token)}`);

      if (data && data.invitation) {
        this.invitation = data.invitation;
        this.guestInfo = data.guest_info;
        this.event = {
          id: data.invitation.event_id,
          title: data.invitation.event_title,
          start_time: data.invitation.start_time,
          is_public: data.invitation.is_public,
          inviter_username: data.invitation.inviter_username
        };
        this.errorMessage = null;
        this.step = 'request-link';
      } else {
        this.renderError(data?.message || 'Invalid invitation token');
      }
    } catch (error) {
      this.logger?.error('Failed to verify token:', error);
      this.renderError('Failed to verify invitation. Please try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async requestMagicLink(deliveryMethod = 'email') {
    try {
      this.loading = true;
      this.render();

      await this.apiRequest(`/events/guest-verify/${encodeURIComponent(this.token)}`, {
        method: 'POST',
        body: JSON.stringify({
          delivery_method: deliveryMethod
        })
      });

      this.step = 'authenticate';
      this.successMessage = `Magic link sent via ${deliveryMethod.toUpperCase()}.`;
    } catch (error) {
      this.logger?.error('Failed to request magic link:', error);
      this.renderError(error?.message || 'Failed to send magic link. Please try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async authenticateGuest(magicToken) {
    try {
      this.loading = true;
      this.render();

      await this.apiRequest(`/events/guest-access/${encodeURIComponent(magicToken)}`);
      this.step = 'rsvp';
      await this.checkGuestSession();
    } catch (error) {
      this.logger?.error('Failed to authenticate guest:', error);
      this.renderError(error?.message || 'Failed to authenticate. Please try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async checkGuestSession() {
    try {
      const data = await this.apiRequest('/events/guest-session');
      if (data.authenticated && data.event) {
        if (!this.event || this.event.id === data.event.id) {
          this.event = {
            ...this.event,
            ...data.event
          };
        }
        this.step = 'rsvp';
      } else {
        this.step = 'request-link';
      }
      this.render();
    } catch (error) {
      this.logger?.error('Failed to check guest session:', error);
    }
  }

  async submitRSVP(status) {
    try {
      this.loading = true;
      this.render();

      const data = await this.apiRequest('/events/guest-rsvp', {
        method: 'POST',
        body: JSON.stringify({
          status
        })
      });

      this.event.user_rsvp = data?.rsvp_status || status;
      this.renderSuccess(`You've RSVP'd ${status === 'yes' ? 'Yes' : status === 'maybe' ? 'Maybe' : 'No'}`);
    } catch (error) {
      this.logger?.error('Failed to submit RSVP:', error);
      this.renderError(error?.message || 'Failed to submit RSVP. Please try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  render() {
    this.innerHTML = `
      <div class="guest-rsvp-container">
        ${this.errorMessage ? this.renderErrorCard(this.errorMessage) : this.renderContent()}
      </div>
    `;
    this.attachEventListeners();
  }

  renderContent() {
    if (this.loading) {
      return `
        <div class="guest-rsvp-loading">
          <div class="spinner"></div>
          <p>Loading...</p>
        </div>
      `;
    }

    if (!this.event && this.step === 'verify') {
      return '';
    }

    const successBanner = this.successMessage ? this.renderSuccessBanner(this.successMessage) : '';

    switch (this.step) {
    case 'request-link':
      return `${successBanner}${this.renderRequestLink()}`;
    case 'authenticate':
      return `${successBanner}${this.renderAuthenticate()}`;
    case 'rsvp':
      return `${successBanner}${this.renderRSVP()}`;
    default:
      return '';
    }
  }

  renderRequestLink() {
    if (!this.event || !this.invitation) {
      return this.renderErrorCard('Event invitation not found.');
    }

    const contactOptions = [];
    if (this.guestInfo?.email) {
      contactOptions.push({
        method: 'email',
        label: `Send to ${this.guestInfo.email}`,
        detail: 'Email magic link'
      });
    }
    if (this.guestInfo?.phone) {
      contactOptions.push({
        method: 'sms',
        label: `Text ${this.formatPhone(this.guestInfo.phone)}`,
        detail: 'SMS magic link'
      });
    }

    return `
      <div class="guest-rsvp-card">
        <h2>Verify Your Invitation</h2>
        <div class="event-preview">
          <h3>${escapeHtml(this.event.title || 'Upcoming event')}</h3>
          ${this.event.start_time ? `
            <p class="event-date">📅 ${this.formatDate(this.event.start_time)}</p>
          ` : ''}
          <p class="event-description">
            Invited by ${escapeHtml(this.event.inviter_username || 'a friend')}
          </p>
        </div>
        <p class="instruction-text">
          To keep invitations secure, we’ll send you a one-time magic link.
          Choose how you’d like us to deliver it.
        </p>
        ${contactOptions.length ? `
          <button class="btn btn-primary" id="requestLinkBtn" data-method="${contactOptions[0].method}">
            Send Magic Link to ${contactOptions[0].method === 'sms' ? 'Phone' : 'Email'}
          </button>
          ${contactOptions.length > 1 ? `
            <div class="contact-options">
              ${contactOptions.map(option => `
                <button class="btn contact-option" data-method="${option.method}">
                  <strong>${option.label}</strong>
                  <span>${option.detail}</span>
                </button>
              `).join('')}
            </div>
          ` : ''}
        ` : `
          <div class="guest-rsvp-card error">
            <p>No contact info on file for this invitation. Please reach out to your host.</p>
          </div>
        `}
      </div>
    `;
  }

  renderAuthenticate() {
    const urlParams = new URLSearchParams(window.location.search);
    const magicToken = urlParams.get('magicToken');

    if (magicToken && !this.magicTokenProcessed) {
      this.magicTokenProcessed = true;
      // Auto-authenticate if magic token is in URL
      this.authenticateGuest(magicToken);
      return `
        <div class="guest-rsvp-loading">
          <div class="spinner"></div>
          <p>Authenticating...</p>
        </div>
      `;
    }

    return `
      <div class="guest-rsvp-card">
        <h2>✅ Magic Link Sent!</h2>
        <p class="instruction-text">
          We've sent a magic link to your email address. 
          Click the link in the email to continue.
        </p>
        <p class="help-text">
          Check your inbox and spam folder. The link will expire in 1 hour.
        </p>
        <button class="btn btn-secondary" id="resendLinkBtn">
          Resend Magic Link
        </button>
      </div>
    `;
  }

  renderRSVP() {
    if (!this.event) {
      return `
        <div class="guest-rsvp-card error">
          <h2>Event Not Available</h2>
          <p>We couldn't load the event details. Please request a new magic link.</p>
        </div>
      `;
    }

    const rsvpStatus = this.event.user_rsvp || 'pending';

    return `
      <div class="guest-rsvp-card">
        <h2>RSVP to Event</h2>
        <div class="event-preview">
          <h3>${escapeHtml(this.event.title)}</h3>
          ${this.event.start_time ? `
            <p class="event-date">📅 ${new Date(this.event.start_time).toLocaleString()}</p>
          ` : ''}
          ${this.event.location ? `
            <p class="event-location">📍 ${escapeHtml(this.event.location)}</p>
          ` : ''}
        </div>
        ${rsvpStatus !== 'pending' ? `
          <div class="current-rsvp">
            <p><strong>Your RSVP:</strong> ${rsvpStatus === 'yes' ? '✅ Yes' : rsvpStatus === 'maybe' ? '🤔 Maybe' : '❌ No'}</p>
          </div>
        ` : ''}
        <div class="rsvp-buttons">
          <button class="btn rsvp-btn rsvp-yes ${rsvpStatus === 'yes' ? 'active' : ''}" 
                  data-status="yes">
            ✅ Yes
          </button>
          <button class="btn rsvp-btn rsvp-maybe ${rsvpStatus === 'maybe' ? 'active' : ''}" 
                  data-status="maybe">
            🤔 Maybe
          </button>
          <button class="btn rsvp-btn rsvp-no ${rsvpStatus === 'no' ? 'active' : ''}" 
                  data-status="no">
            ❌ No
          </button>
        </div>
        ${rsvpStatus !== 'pending' ? `
          <p class="help-text">You can change your RSVP at any time.</p>
        ` : ''}
      </div>
    `;
  }

  renderSuccess(message) {
    this.successMessage = message;
    this.errorMessage = null;
    this.render();
  }

  renderError(message) {
    this.errorMessage = message;
    this.render();
  }

  renderErrorCard(message) {
    return `
      <div class="guest-rsvp-card error">
        <h2>Something went wrong</h2>
        <p>${escapeHtml(message)}</p>
        <button class="btn btn-secondary" onclick="window.location.href='/'">
          Go Home
        </button>
      </div>
    `;
  }

  renderSuccessBanner(message) {
    return `
      <div class="guest-rsvp-banner success">
        ${escapeHtml(message)}
      </div>
    `;
  }

  attachEventListeners() {
    const primaryButton = this.querySelector('#requestLinkBtn');
    if (primaryButton) {
      primaryButton.addEventListener('click', (event) => {
        const method = event.currentTarget.dataset.method || 'email';
        this.requestMagicLink(method);
      });
    }

    const contactButtons = this.querySelectorAll('.contact-option');
    contactButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const method = event.currentTarget.dataset.method;
        this.requestMagicLink(method);
      });
    });

    const resendBtn = this.querySelector('#resendLinkBtn');
    if (resendBtn) {
      resendBtn.addEventListener('click', () => {
        this.step = 'request-link';
        this.render();
      });
    }

    const rsvpBtns = this.querySelectorAll('.rsvp-btn');
    rsvpBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const status = e.target.dataset.status;
        this.submitRSVP(status);
      });
    });

    // Check for magic token in URL on page load
    if (this.step === 'authenticate' && !this.magicTokenProcessed) {
      const urlParams = new URLSearchParams(window.location.search);
      const magicToken = urlParams.get('magicToken');
      if (magicToken) {
        this.magicTokenProcessed = true;
        this.authenticateGuest(magicToken);
      }
    }
  }


  formatDate(dateString) {
    try {
      return new Date(dateString).toLocaleString();
    } catch (_error) {
      return dateString;
    }
  }

  formatPhone(phone) {
    if (!phone) return '';
    const sanitized = phone.replace(/[^\d]/g, '');
    if (sanitized.length === 10) {
      return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6)}`;
    }
    return phone;
  }

  async apiRequest(endpoint, options = {}) {
    try {
      if (window.API?.request) {
        return await window.API.request(endpoint, options);
      }

      const response = await fetch(`/api${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {})
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || errorData.error || 'Request failed';
        const error = new Error(message);
        error.details = errorData.details;
        throw error;
      }

      return await response.json();
    } catch (error) {
      this.logger?.error('Guest RSVP API error', error, { endpoint });
      throw error;
    }
  }
}

customElements.define('guest-rsvp', GuestRSVP);

